// =====================================================================
//  Éditeur d'une stratégie OTP — TabStrip à 5 onglets
//  ---------------------------------------------------------------------
//  Implémente IComponentPanelObject pour être hébergé dans un ComponentPanel
//  (comme TemplateEditor). En Lot 1, les onglets sont des conteneurs vides ;
//  le Lot 2 y branchera les sous-éditeurs (Généralités / Code / Message /
//  Contrat d'appel).
// =====================================================================

import {IComponentPanelObject} from "../ComponentPanel.js";
import {IOtpStrategyModel} from "../../Model/Otp/OtpStrategyModel.js";
import {KendoToolBarHandler} from "../../Tools/Misc.js";
import {NotificationHelper} from "../../Helper/NotificationHelper.js";
import {OtpUpsert} from "../../Controller/Otp/OtpUpsert.js";
import {OtpDelete} from "../../Controller/Otp/OtpDelete.js";
import {RessourceSlot} from "../../Tools/Disposable/RessourceSlot";
import {DisposableStack} from "../../Tools/Disposable/DisposableStack";
import {OtpGeneraliteEditor} from "./OtpGeneraliteEditor.js";
import {OtpCodeEditor} from "./OtpCodeEditor.js";
import {OtpMessageEditor} from "./OtpMessageEditor.js";
import {OtpContractEditor} from "./OtpContractEditor.js";
import {IEasymailPackage} from "../../Model/Package/IEasymailPackage.js";
import {PackageImportDialog} from "../Package/PackageImportDialog.js";
import {PackageExportDialog} from "../Package/PackageExportDialog.js";
import {PackageMapper} from "../../Tools/Package/PackageMapper.js";
import {PackageImporter} from "../../Tools/Package/PackageImporter.js";

interface ITabDef {
    action: string;
    label: string;
}

const TABS: ITabDef[] = [
    {action: "generalite", label: "Généralités"},
    {action: "code", label: "Code"},
    {action: "message", label: "Message"},
    {action: "contrat", label: "Contrat d'appel"},
];

export class OtpEditor implements IComponentPanelObject {
    public MainContainer: JQuery | undefined;
    public Titre: string | undefined;

    #jqContainer: JQuery | undefined;
    #jqTabContainer: JQuery | undefined;
    #jqToolbar: JQuery | undefined;
    #kTab: kendo.ui.TabStrip | undefined;
    #tabDivs = new Map<string, JQuery>();

    readonly #componentStack = new DisposableStack();
    readonly #generaliteSlot = new RessourceSlot<OtpGeneraliteEditor>();
    readonly #codeSlot = new RessourceSlot<OtpCodeEditor>();
    readonly #messageSlot = new RessourceSlot<OtpMessageEditor>();
    readonly #contractSlot = new RessourceSlot<OtpContractEditor>();

    readonly #model: IOtpStrategyModel;

    constructor(options: IOtpEditorOption) {
        this.#model = options.model;
        if (options.jqMainContainer) this.MainContainer = options.jqMainContainer;
        this.Titre = options.model?.Nom;

        this.#componentStack.Use(this.#generaliteSlot);
        this.#componentStack.Use(this.#codeSlot);
        this.#componentStack.Use(this.#messageSlot);
        this.#componentStack.Use(this.#contractSlot);
    }

    public Init = async () => {
        this.#buildHtml();
        this.#buildTabs();
        this.#buildToolbar();
        this.#kTab.select(0);
    };

    #buildHtml = () => {
        this.#jqContainer = $("<div></div>")
            .css({
                height: "100%",
                width: "100%",
                margin: 0,
                padding: 0,
                display: "flex",
                flexDirection: "column",
            })
            .appendTo(this.MainContainer);

        this.#jqTabContainer = $("<div></div>")
            .css({display: "flex", flexGrow: "1"})
            .appendTo(this.#jqContainer);

        const ul = $("<ul></ul>").appendTo(this.#jqTabContainer);
        for (const t of TABS) {
            ul.append(`<li circ-action='${t.action}'>${t.label}</li>`);
            const div = $("<div></div>").attr("circ-id", t.action);
            this.#tabDivs.set(t.action, div);
            this.#jqTabContainer.append(div);
        }

        // Conteneur toolbar (bas de l'éditeur).
        const toolWrap = $("<div></div>").css({width: "100%"}).appendTo(this.#jqContainer);
        this.#jqToolbar = $("<div></div>").appendTo(toolWrap);
    };

    #buildTabs = () => {
        this.#kTab = this.#jqTabContainer
            .kendoTabStrip({
                animation: false,
                select: (e: kendo.ui.TabStripSelectEvent) => {
                    const action = $(e.item).attr("circ-action");
                    this.#renderTab(action);
                },
            })
            .data("kendoTabStrip");
    };

    /** Instancie (à la demande) le sous-éditeur correspondant à l'onglet. */
    #renderTab = (action?: string) => {
        if (!action) return;
        const div = this.#tabDivs.get(action);
        if (!div) return;
        if (div.children().length > 0) return; // déjà rendu

        switch (action) {
            case "generalite":
                this.#generaliteSlot
                    .Reset(() => new OtpGeneraliteEditor({jqMainContainer: div, model: this.#model}))
                    .Init();
                break;
            case "code":
                this.#codeSlot
                    .Reset(() => new OtpCodeEditor({jqMainContainer: div, model: this.#model}))
                    .Init();
                break;
            case "message":
                this.#messageSlot
                    .Reset(() => new OtpMessageEditor({jqMainContainer: div, model: this.#model}))
                    .Init();
                break;
            case "contrat":
                this.#contractSlot
                    .Reset(() => new OtpContractEditor({jqMainContainer: div, model: this.#model}))
                    .Init();
                break;
        }
    };

    #buildToolbar = () => {
        this.#jqToolbar.kendoToolBar({
            size: "small",
            items: [
                {
                    type: "button",
                    text: "Enregistrer",
                    icon: "save",
                    themeColor: "success",
                    click: async (e: any) =>
                        KendoToolBarHandler.AsyncToolbarHandle(e.target, this.#save),
                },
                {
                    type: "button",
                    text: "Supprimer",
                    icon: "trash",
                    themeColor: "error",
                    click: async () => this.#delete(),
                },
                {
                    type: "button",
                    text: "Importer",
                    icon: "upload",
                    click: async () => this.#import(),
                },
                {
                    type: "button",
                    text: "Exporter",
                    icon: "download",
                    click: async (e: any) =>
                        KendoToolBarHandler.AsyncToolbarHandle(e.target, this.#export),
                },
            ],
        });
    };

    #save = async () => {
        const saved = await OtpUpsert.Upsert(this.#model);
        if (!saved) return; // erreur déjà notifiée par le controller
        this.#model.Primarykey = saved.Primarykey;
        this.Titre = saved.Nom;
        NotificationHelper.Instance.success("Stratégie OTP enregistrée");
    };

    // --- Import / export d'un paquet « easymail-template/1 » ------------------
    // Ici, seule la section « otpStrategy » est appliquée (sans enregistrer) :
    // le contenu du template e-mail se met à jour depuis l'éditeur du template.
    // L'export, lui, emporte la stratégie ET son template e-mail.

    #import = async () => {
        const pkg = await PackageImportDialog.Open({
            title: `Importer dans « ${this.#model.Nom} »`,
            intro: "Remplace la configuration de cette stratégie (code, durée de vie, tentatives, canaux) par celle " +
                "d'un paquet. Rien n'est enregistré avant que vous ne cliquiez sur « Enregistrer ».",
            context: {mode: "update-otp", currentId: this.#model.Primarykey, fkProjet: this.#model.FkProjet},
            describe: this.#describeImport,
            confirmText: "Remplacer la configuration",
        });
        if (!pkg?.otpStrategy)
            return;

        this.ApplyPackage(pkg);
        NotificationHelper.Instance.info("Configuration importée : vérifiez-la puis enregistrez.");
    };

    /** Applique la section otpStrategy au modèle (en place) et reconstruit les onglets. */
    public ApplyPackage = (pkg: IEasymailPackage) => {
        if (!pkg.otpStrategy)
            return;
        PackageMapper.ApplyOtpStrategy(pkg.otpStrategy, this.#model);

        // Les sous-éditeurs lisent le modèle à leur construction : on les
        // dispose (ce qui vide leur conteneur) puis on réaffiche l'onglet actif.
        [this.#generaliteSlot, this.#codeSlot, this.#messageSlot, this.#contractSlot].forEach(slot => slot.Clear());
        this.#tabDivs.forEach(div => div.empty());
        this.#renderTab($(this.#kTab?.select()).attr("circ-action"));
    };

    #describeImport = (pkg: IEasymailPackage): Array<string> => {
        const s = pkg.otpStrategy;
        if (!s)
            return [];
        const after: IOtpStrategyModel = {...this.#model};
        PackageMapper.ApplyOtpStrategy(s, after);
        const lines: Array<string> = [];
        const fields: Array<[string, keyof IOtpStrategyModel]> = [
            ["Nom", "Nom"], ["Libellé", "Libelle"], ["Longueur", "PatternLength"], ["Alphabet", "PatternAlphabet"],
            ["Casse", "PatternCase"], ["Taille de groupe", "PatternGroupSize"], ["Séparateur", "PatternGroupSeparator"],
            ["Durée de vie (min)", "ExpirationMinutes"], ["Tentatives", "MaxAttempts"], ["Canaux", "Channels"],
        ];
        fields.forEach(([label, key]) => {
            if ((this.#model[key] ?? "") !== (after[key] ?? ""))
                lines.push(`${label} : « ${this.#model[key] ?? ""} » → « ${after[key] ?? ""} »`);
        });
        if (!lines.length)
            lines.push("Configuration identique : rien ne change.");
        lines.push("Le template e-mail lié n'est pas modifié ici. Rien n'est enregistré avant « Enregistrer ».");
        return lines;
    };

    #export = async () => {
        if (!this.#model.FkMailTemplate) {
            NotificationHelper.Instance.error("Associez un template e-mail (onglet Message) avant d'exporter : il fait partie du paquet.");
            return;
        }
        const pivot = await PackageImporter.ReadGroup(this.#model.FkMailTemplate);
        const translations = pivot.Translations ?? [];
        PackageExportDialog.Open({
            title: `Exporter la stratégie « ${this.#model.Nom} »`,
            translationCount: translations.length,
            build: (withTranslations) => PackageMapper.FromModels({
                pivot,
                translations: withTranslations ? translations : [],
                otpStrategy: this.#model,
            }),
        });
    };

    #delete = async () => {
        if (!this.#model.Primarykey) {
            NotificationHelper.Instance.info("Cette stratégie n'est pas encore enregistrée.");
            return;
        }
        kendo
            .confirm("Bien certain de vouloir supprimer cette stratégie OTP ?")
            .then(async () => {
                await OtpDelete.Delete(this.#model);
                NotificationHelper.Instance.success("Stratégie OTP supprimée");
            });
    };

    public Dispose = () => {
        this.#componentStack.Dispose();
        if (this.#kTab) this.#kTab.destroy();
        if (this.MainContainer) this.MainContainer.empty();
    };
}

export interface IOtpEditorOption {
    jqMainContainer?: JQuery;
    model: IOtpStrategyModel;
}
